import React, { Fragment, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Menu as HeadlessMenu, Transition } from "@headlessui/react";
import { FiChevronDown, FiLogOut, FiMoon, FiSun } from "react-icons/fi";
import { MdLockOutline } from "react-icons/md";

import { SharedContext } from "context/SharedContext";
import { actionLogout } from "redux/auth/AuthSlice";
import { selectLoguedUser } from "redux/auth/AuthSelector";
import { GetBreadcrumb } from "./MenuConfig";

const GetInitials = (sName = "") =>
  sName
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(sWord => sWord[0]?.toUpperCase() ?? "")
    .join("") || "?";

/**
 * Barra superior: ubicación actual a la izquierda; tema claro/oscuro y menú del usuario a la derecha.
 */
const TopBar = () => {
  const oDispatch = useDispatch();
  const fnNavigate = useNavigate();
  const { pathname: sPathname } = useLocation();
  const { Logout, bDarkTheme, setDarkTheme } = useContext(SharedContext);
  const oUser = useSelector(selectLoguedUser);

  const aCrumbs = GetBreadcrumb(sPathname);

  const onLogout = () => {
    Logout();
    oDispatch(actionLogout());
  };

  const sMenuItemClass = active =>
    `${active ? "bg-gray-100 dark:bg-gray-700" : ""} flex w-full items-center gap-2 rounded px-2.5 py-1.5 text-sm text-gray-700 dark:text-gray-200`;

  return (
    <header className="sticky top-0 z-30 flex items-center justify-between gap-3 h-12 px-5 md:px-10 bg-white/90 backdrop-blur border-b border-line dark:bg-gray-900/90 dark:border-line-dark">
      <nav aria-label="Ubicación" className="min-w-0 text-xs text-gray-400 truncate">
        {aCrumbs.map((sCrumb, nIndex) => (
          <span key={`${sCrumb}_${nIndex}`}>
            {nIndex > 0 && <span className="mx-1.5">/</span>}
            <span className={nIndex === aCrumbs.length - 1 ? "text-gray-600 dark:text-gray-300" : ""}>{sCrumb}</span>
          </span>
        ))}
      </nav>

      <div className="flex items-center gap-1 shrink-0">
        <button
          type="button"
          title={bDarkTheme ? "Tema claro" : "Tema oscuro"}
          onClick={() => setDarkTheme(!bDarkTheme)}
          className="p-2 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white"
        >
          {bDarkTheme ? <FiSun size={16} /> : <FiMoon size={16} />}
        </button>

        <HeadlessMenu as="div" className="relative">
          <HeadlessMenu.Button className="flex items-center gap-2 rounded-md py-1 pl-1 pr-2 hover:bg-gray-100 dark:hover:bg-gray-800">
            <span className="flex items-center justify-center h-7 w-7 rounded-full bg-primary-50 text-primary-700 text-xs font-medium dark:bg-gray-700 dark:text-primary-200">
              {GetInitials(oUser?.name)}
            </span>
            <span className="hidden md:block text-sm text-gray-700 dark:text-gray-200 max-w-[10rem] truncate">{oUser?.name}</span>
            <FiChevronDown className="hidden md:block h-4 w-4 text-gray-400" />
          </HeadlessMenu.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <HeadlessMenu.Items className="absolute right-0 mt-1 w-56 rounded-md bg-white dark:bg-gray-800 border border-line dark:border-line-dark shadow-md p-1 focus:outline-none">
              <div className="px-2.5 py-2 border-b border-line dark:border-line-dark mb-1">
                <p className="text-sm font-medium text-gray-800 dark:text-white truncate">{oUser?.name}</p>
                <p className="text-xs text-gray-400 truncate">{oUser?.username}</p>
              </div>
              <HeadlessMenu.Item>
                {({ active }) => (
                  <button type="button" className={sMenuItemClass(active)} onClick={() => fnNavigate("/change-password")}>
                    <MdLockOutline size={16} className="text-gray-400" />
                    Cambiar contraseña
                  </button>
                )}
              </HeadlessMenu.Item>
              <HeadlessMenu.Item>
                {({ active }) => (
                  <button type="button" className={sMenuItemClass(active)} onClick={onLogout}>
                    <FiLogOut size={16} className="text-gray-400" />
                    Cerrar sesión
                  </button>
                )}
              </HeadlessMenu.Item>
            </HeadlessMenu.Items>
          </Transition>
        </HeadlessMenu>
      </div>
    </header>
  );
};

export default TopBar;
